import {
  prepListsResponseSchema,
  batchPlansResponseSchema,
  type PrepListsResponse,
  type PrepListStatus,
  type BatchPlansResponse,
  type BatchPlanStatus,
} from '../types';
import {
  ApiClientError, buildUrl 
} from '@/errors';
import {
  authFetch, API_BASE_URL 
} from '@/lib/api-client';

export interface PrepListFilters {
  readonly date?: string;
  readonly venueId?: string;
  readonly status?: PrepListStatus;
}

export async function getPrepLists(siteId: string, filters?: PrepListFilters): Promise<PrepListsResponse> {
  const params = new URLSearchParams();
  if (filters?.date) params.set('date', filters.date);
  if (filters?.venueId) params.set('venueId', filters.venueId);
  if (filters?.status) params.set('status', filters.status);

  const url = buildUrl(API_BASE_URL, `/v1/sites/${siteId}/prep-lists`, params);
  const response = await authFetch(url);
  
  if (!response.ok) {
    throw ApiClientError.fromResponse(response, 'Failed to fetch prep lists');
  }

  return prepListsResponseSchema.parse(await response.json());
}

// Batch Planning API

export interface BatchPlanFilters {
  readonly date?: string;
  readonly venueId?: string;
  readonly status?: BatchPlanStatus;
}

export interface CreateBatchPlanInput {
  readonly name: string;
  readonly venueId: string;
  readonly date: string;
  readonly startTime: string;
  readonly endTime: string;
  readonly notes?: string;
}

export async function getBatchPlans(siteId: string, filters?: BatchPlanFilters): Promise<BatchPlansResponse> {
  const params = new URLSearchParams();
  if (filters?.date) params.set('date', filters.date);
  if (filters?.venueId) params.set('venueId', filters.venueId);
  if (filters?.status) params.set('status', filters.status);

  const url = buildUrl(API_BASE_URL, `/v1/sites/${siteId}/batch-plans`, params);
  const response = await authFetch(url);
  
  if (!response.ok) {
    throw ApiClientError.fromResponse(response, 'Failed to fetch batch plans');
  }

  return batchPlansResponseSchema.parse(await response.json());
}

export async function createBatchPlan(
  siteId: string,
  input: CreateBatchPlanInput
): Promise<{ batchPlanId: string }> {
  const url = buildUrl(API_BASE_URL, `/v1/sites/${siteId}/batch-plans`);
  const response = await authFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  
  if (!response.ok) {
    throw ApiClientError.fromResponse(response, 'Failed to create batch plan');
  }

  return response.json() as Promise<{ batchPlanId: string }>;
}
